import { type FC } from "react";

import { Card } from "@/components/ui/Card";

import { type OuraWorkout } from "../../data/ouraTypes";

/**
 * WHAT:
 *   Returns color classes for workout intensity levels.
 *
 * WHY:
 *   Provides consistent color coding for intensity visualization.
 *
 * @param intensity - Workout intensity string
 *
 * @returns Object with background and text color classes
 *
 */
const getIntensityColor = (intensity: string) => {
  switch (intensity.toLowerCase()) {
    case "high": {
      return { bgColor: "bg-red-900/20", color: "text-red-400" };
    }
    case "moderate": {
      return { bgColor: "bg-orange-900/20", color: "text-orange-400" };
    }
    case "low": {
      return { bgColor: "bg-blue-900/20", color: "text-blue-400" };
    }
    case "very_low": {
      return { bgColor: "bg-green-900/20", color: "text-green-400" };
    }
    default: {
      return { bgColor: "bg-neutral-700", color: "text-neutral-400" };
    }
  }
};

interface WorkoutSummaryCardProperties {
  className?: string;
  intensityMinutes?: Record<string, number>;
  totalCalories?: number;
  workouts?: OuraWorkout[];
}

/**
 * WorkoutSummaryCard - Zeigt Workout-Zusammenfassung und Details
 *
 * Features:
 * - Verwendet globale Card-Komponente für konsistentes Styling
 * - Anzahl der Workouts
 * - Gesamtkalorien für heute
 * - Intensitäts-Minuten (verschiedene Intensitäten)
 * - Workout-Details
 *
 * @param root0
 *
 * @param root0.workouts
 *
 * @param root0.intensityMinutes
 *
 * @param root0.className
 *
 * @returns Workout summary card component
 *
 */
export const WorkoutSummaryCard: FC<WorkoutSummaryCardProperties> = ({
  className = "",
  intensityMinutes: propertyIntensityMinutes,
  workouts = [],
}) => {
  const workoutCount = workouts.length;

  // Berechne Gesamtkalorien für heute
  const todayCalories = workouts.reduce((sum, workout) => sum + (workout.calories ?? 0), 0);

  // Berechne Intensitäts-Minuten
  const calculateIntensityMinutes = () => {
    const intensityMap: Record<string, number> = {};

    for (const workout of workouts) {
      const startTime = workout.start_datetime ?? workout.start_time;
      const endTime = workout.end_datetime ?? workout.end_time;
      const intensity = workout.intensity ?? "unknown";

      if (startTime && endTime) {
        const start = new Date(startTime);
        const end = new Date(endTime);
        const durationMinutes = Math.round((end.getTime() - start.getTime()) / (1000 * 60));

        intensityMap[intensity] = (intensityMap[intensity] ?? 0) + durationMinutes;
      }
    }

    return intensityMap;
  };

  // Verwende propIntensityMinutes falls verfügbar, sonst berechne lokal
  const intensityMinutes = propertyIntensityMinutes ?? calculateIntensityMinutes();

  return (
    <Card className={className} padding="sm" variant="default">
      <div className="mb-1.5">
        <h3 className="text-sm font-medium text-neutral-200">Training & Aktivität</h3>
      </div>

      {/* Hauptmetriken */}
      <div className="grid grid-cols-2 gap-3 mb-2">
        <div className="text-center">
          <div className="text-lg font-bold text-neutral-100">{workoutCount}</div>
          <div className="text-xs text-neutral-400">Workouts</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-neutral-100">
            {todayCalories > 0 ? todayCalories.toFixed(0) : "N/A"}
          </div>
          <div className="text-xs text-neutral-400">Kalorien</div>
        </div>
      </div>

      {/* Intensitäts-Minuten */}
      {Object.keys(intensityMinutes).length > 0 && (
        <div className="mb-2">
          <div className="text-xs text-neutral-400 mb-1">Intensitäts-Minuten</div>
          <div className="space-y-1">
            {Object.entries(intensityMinutes).map(([intensity, minutes]) => {
              const intensityColor = getIntensityColor(intensity);
              return (
                <div className="flex justify-between items-center text-xs" key={intensity}>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${intensityColor.bgColor} ${intensityColor.color}`}
                  >
                    {intensity}
                  </span>
                  <span className="text-neutral-200 font-medium">{minutes} min</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Workout Details */}
      {workouts.length > 0 && (
        <div className="mt-2 pt-2 border-t border-neutral-700">
          <h4 className="text-xs font-medium text-neutral-200 mb-1">Letzte Aktivitäten</h4>
          <div className="space-y-1 max-h-20 overflow-y-auto">
            {workouts.slice(0, 2).map((workout) => {
              const startTime = workout.start_datetime ?? workout.start_time;
              const endTime = workout.end_datetime ?? workout.end_time;

              if (!startTime || !endTime) {
                return null;
              }

              const start = new Date(startTime);
              const end = new Date(endTime);
              const duration = Math.round((end.getTime() - start.getTime()) / (1000 * 60)); // Minuten

              return (
                <div className="flex justify-between items-center text-xs" key={workout.id}>
                  <div className="flex-1">
                    <div className="font-medium text-neutral-200">
                      {workout.activity ?? workout.label ?? "Aktivität"}
                    </div>
                    <div className="text-neutral-400">
                      {start.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}{" "}
                      - {duration}min
                    </div>
                  </div>
                  <div className="text-right">
                    {workout.calories ? (
                      <div className="font-medium text-neutral-200">
                        {workout.calories.toFixed(0)} cal
                      </div>
                    ) : null}
                    {workout.intensity ? (
                      <div className="text-neutral-400">{workout.intensity}</div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
          {workouts.length > 2 && (
            <div className="text-xs text-neutral-500 mt-1 text-center">
              +{workouts.length - 2} weitere
            </div>
          )}
        </div>
      )}

      {/* Keine Workouts */}
      {workouts.length === 0 && (
        <div className="text-center py-3">
          <div className="text-sm text-neutral-400">Keine Aktivitäten heute</div>
          <div className="text-xs text-neutral-500 mt-1">Zeit für Bewegung! 💪</div>
        </div>
      )}
    </Card>
  );
};
